import { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import { AlertsIcon } from '../components/Icons';
import { fetchPortfolio, fetchPrice, detectCurrency, cadToUsd } from '../utils/api';

function AlertsSimulator() {
  const [alerts, setAlerts] = useState([]);
  const [alertTicker, setAlertTicker] = useState('');
  const [alertPrice, setAlertPrice] = useState(150);
  const [alertDir, setAlertDir] = useState('Above');
  const [alertMsg, setAlertMsg] = useState(null);
  const [alertData, setAlertData] = useState([]);
  const [checkingAlerts, setCheckingAlerts] = useState(false);

  const [portfolio, setPortfolio] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [dropPct, setDropPct] = useState(20);
  const [simLoading, setSimLoading] = useState(false);

  useEffect(() => { loadPortfolio(); }, []);

  async function loadPortfolio() {
    const p = await fetchPortfolio();
    setPortfolio(p);
    if (p.length > 0) {
      setSimLoading(true);
      let total = 0;
      for (const h of p) {
        const priceData = await fetchPrice(h.ticker);
        const price = priceData ? priceData.price : h.buy_price;
        const val = price * h.shares;
        total += detectCurrency(h.ticker) === 'CAD' ? cadToUsd(val) : val;
      }
      setTotalValue(total);
      setSimLoading(false);
    }
  }

  async function handleAddAlert() {
    if (!alertTicker.trim()) { setAlertMsg({ type: 'error', text: 'Enter a ticker symbol.' }); return; }
    const newAlert = { ticker: alertTicker.trim().toUpperCase(), target: alertPrice, direction: alertDir };
    setAlerts(prev => [...prev, newAlert]);
    setAlertMsg({ type: 'success', text: `Alert set: ${newAlert.ticker} ${alertDir} ${alertPrice.toFixed(2)}` });
    setAlertTicker('');
    setTimeout(() => setAlertMsg(null), 3000);
  }

  async function checkAlerts() {
    setCheckingAlerts(true);
    const data = [];
    const priceCache = {};
    for (const a of alerts) {
      if (!priceCache[a.ticker]) {
        const p = await fetchPrice(a.ticker);
        priceCache[a.ticker] = p ? p.price : null;
      }
      const cp = priceCache[a.ticker];
      let triggered = false;
      if (cp) {
        if (a.direction === 'Above' && cp >= a.target) triggered = true;
        if (a.direction === 'Below' && cp <= a.target) triggered = true;
      }
      data.push({ ...a, currentPrice: cp, triggered });
    }
    setAlertData(data);
    setCheckingAlerts(false);
  }

  useEffect(() => { if (alerts.length > 0) checkAlerts(); }, [alerts]);

  const estimatedLoss = totalValue * (dropPct / 100);
  const remaining = totalValue - estimatedLoss;
  const severity = dropPct <= 10 ? 'low' : dropPct <= 20 ? 'moderate' : 'severe';
  const severityClass = severity === 'low' ? 'alert-info' : severity === 'moderate' ? 'alert-warning' : 'alert-error';

  return (
    <div>
      <div className="page-title">
        <AlertsIcon size={28} color="#00ff9d" />
        <h1>Alerts & Simulator</h1>
      </div>
      <p className="hero-subtitle">Set price alerts and simulate market crash scenarios</p>

      <h3 style={{ marginBottom: 12 }}>Price Alert Creator</h3>
      {alertMsg && <div className={`alert-box alert-${alertMsg.type}`}>{alertMsg.text}</div>}

      <div className="glass-card">
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: 16 }}>
          <div className="form-group">
            <label className="form-label">Ticker</label>
            <input className="input-field" value={alertTicker} onChange={e => setAlertTicker(e.target.value)}
              placeholder="AAPL or SHOP.TO" />
          </div>
          <div className="form-group">
            <label className="form-label">Target Price</label>
            <input className="input-field" type="number" min="0.01" step="0.50" value={alertPrice}
              onChange={e => setAlertPrice(parseFloat(e.target.value) || 0)} />
          </div>
          <div className="form-group">
            <label className="form-label">Direction</label>
            <select className="input-field" value={alertDir} onChange={e => setAlertDir(e.target.value)}>
              <option value="Above">Above</option>
              <option value="Below">Below</option>
            </select>
          </div>
        </div>
      </div>

      <button className="btn-primary" onClick={handleAddAlert} style={{ marginBottom: 16 }}>Add Alert</button>

      {alerts.length > 0 ? (
        <>
          <h4 style={{ color: '#e2e8f0', marginBottom: 8 }}>Active Alerts</h4>
          <div className="glass-card" style={{ padding: 0, overflow: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th>Direction</th>
                  <th>Target Price</th>
                  <th>Current Price</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(alertData.length > 0 ? alertData : alerts.map(a => ({ ...a, currentPrice: null, triggered: false }))).map((a, i) => (
                  <tr key={i}>
                    <td style={{ color: '#e2e8f0', fontWeight: 600 }}>{a.ticker}</td>
                    <td>{a.direction}</td>
                    <td>{a.target.toFixed(2)}</td>
                    <td>{a.currentPrice ? a.currentPrice.toFixed(2) : 'N/A'}</td>
                    <td className={a.triggered ? 'loss' : 'gain'}>{a.triggered ? 'TRIGGERED' : 'Watching'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="btn-secondary" onClick={() => { setAlerts([]); setAlertData([]); }} style={{ marginTop: 8 }}>
            Clear All Alerts
          </button>
        </>
      ) : (
        <div className="alert-box alert-info">No alerts set yet. Create one above!</div>
      )}

      <hr />

      <h3 style={{ marginBottom: 12 }}>Market Drop Simulator</h3>
      <p style={{ color: '#94a3b8', marginBottom: 16 }}>Estimate your portfolio loss under different crash scenarios.</p>

      {portfolio.length === 0 ? (
        <div className="glass-card empty-state" style={{ padding: '30px 20px' }}>
          <p style={{ color: '#475569' }}>Add holdings in Portfolio Manager to use the simulator.</p>
        </div>
      ) : simLoading ? (
        <div className="loading-text"><span className="spinner" />Computing portfolio value...</div>
      ) : (
        <>
          <div className="form-group">
            <label className="form-label">Simulated Market Drop: {dropPct}%</label>
            <input type="range" min="5" max="40" step="1" value={dropPct}
              onChange={e => setDropPct(parseInt(e.target.value))}
              style={{ width: '100%', accentColor: '#00ff9d' }} />
          </div>

          <div className="metrics-row">
            <div className="metric-card">
              <div className="metric-label">Current Value (USD)</div>
              <div className="metric-value">${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Estimated Loss ({dropPct}%)</div>
              <div className="metric-value" style={{ color: '#ff4d4d' }}>-${estimatedLoss.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Remaining Value</div>
              <div className="metric-value">${remaining.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          </div>

          <div className={`alert-box ${severityClass}`}>
            A <strong>{dropPct}% market drop</strong> would result in an estimated <strong>${estimatedLoss.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD loss</strong> on your portfolio.
            Severity: <strong>{severity.toUpperCase()}</strong>.
            {severity === 'severe' ? ' Consider holding cash reserves to buy the dip.' : ' Ensure your risk tolerance aligns with this scenario.'}
          </div>

          <Plot
            data={[{
              type: 'bar',
              x: ['Current Value', `After ${dropPct}% Drop`],
              y: [totalValue, remaining],
              marker: {
                color: ['#00ff9d', '#ff4d4d'],
                cornerradius: 8,
              },
              text: [`$${totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, `$${remaining.toLocaleString(undefined, { maximumFractionDigits: 0 })}`],
              textposition: 'outside',
              textfont: { color: '#cbd5e1', size: 14 },
            }]}
            layout={{
              paper_bgcolor: 'rgba(0,0,0,0)',
              plot_bgcolor: 'rgba(0,0,0,0)',
              font: { color: '#94a3b8', family: 'Inter, sans-serif' },
              margin: { t: 20, b: 40, l: 20, r: 20 },
              height: 320,
              yaxis: { showgrid: false, zeroline: false, showticklabels: false },
              xaxis: { showgrid: false, tickfont: { color: '#94a3b8', size: 13 } },
              bargap: 0.4,
            }}
            config={{ displayModeBar: false }}
            style={{ width: '100%' }}
          />
        </>
      )}
    </div>
  );
}

export default AlertsSimulator;
